import React from "react";
import { FloatNav, FloatNavGroup, FloatNavLink } from "../../../components";
import {
  useTranslate,
  planDataUtils,
  getAdditionalBenefit,
} from "../../../helpers";
import { Plan, PlanDetailsSection as PDS } from "../../../@types";

const Navigation: React.FunctionComponent<{
  hasAbstractBenefits: boolean;
  hasDrugs: boolean;
  hasOptionalBenefits: boolean;
  plan: Plan;
  hasDrugCosts: boolean;
}> = ({
  hasDrugs,
  hasOptionalBenefits,
  plan,
  hasAbstractBenefits,
  hasDrugCosts,
}) => {
  const t = useTranslate();
  const hasSpecialBenefits =
    plan.additional_supplemental_benefits &&
    !!plan.additional_supplemental_benefits.special_benefits.length;
  const hasMoreBenefits =
    plan.additional_supplemental_benefits &&
    !!plan.additional_supplemental_benefits.other_benefits.length;
  const hasOpioidTreatmentServices =
    true ||
    getAdditionalBenefit(
      plan,
      "other_benefits",
      "opioid_treatment_services",
      "medicare_covered_benefits"
    ) !== null;
  const onlyMoreBenefitIsOpioidTreatment =
    plan.additional_supplemental_benefits &&
    plan.additional_supplemental_benefits.other_benefits.length === 1 &&
    hasOpioidTreatmentServices;

  return (
    <FloatNav>
      <FloatNavGroup header={t("plan_terms.overview")}>
        <FloatNavLink id={PDS.PREMIUM} label={t("plan_terms.premium")} />
        <FloatNavLink id={PDS.DEDUCTIBLE} label={t("plan_terms.deductible")} />
        <FloatNavLink
          id={PDS.ESTIMATED_YEARLY_COSTS}
          label={t("plan_terms.estimated_yearly_costs")}
        />
        <FloatNavLink id={PDS.MAX_OOPC} label={t("plan_terms.oopc_maximum")} />
      </FloatNavGroup>
      <FloatNavGroup header={t("plan_terms.benefits_and_costs")}>
        <FloatNavLink
          id={PDS.DOCTOR_SERVICES}
          label={t("plan_terms.doctor_services")}
        />
        <FloatNavLink
          id={PDS.TESTS_LABS_IMAGING}
          label={t("plan_terms.tests_labs_imaging")}
        />
        <FloatNavLink
          id={PDS.HOSPITAL_SERVICES}
          label={t("plan_terms.hospital_services")}
        />
        <FloatNavLink
          id={PDS.SKILLED_NURSING}
          label={t("plan_terms.skilled_nursing")}
        />
        <FloatNavLink
          id={PDS.PREVENTIVE_SERVICES}
          label={t("plan_terms.preventive_services")}
        />
        <FloatNavLink id={PDS.AMBULANCE} label={t("plan_terms.ambulance")} />
        <FloatNavLink
          id={PDS.THERAPY_SERVICES}
          label={t("plan_terms.therapy_services")}
        />
        <FloatNavLink
          id={PDS.MENTAL_HEALTH_SERVICES}
          label={t("plan_terms.mental_health")}
        />
        {hasOpioidTreatmentServices && (
          <FloatNavLink
            id={PDS.OPIOID_TREATMENT}
            label={t("more_benefits.opioid_treatment")}
          />
        )}
        <FloatNavLink
          id={PDS.OTHER_SERVICES}
          label={t("plan_terms.other_services")}
        />
        {planDataUtils.showMMPBenefits(plan) && (
          <FloatNavLink
            id={PDS.MMP_HOME_HEALTH_SERVICES}
            label={t("plan_terms.navigation.mmp_home_health_services")}
          />
        )}
      </FloatNavGroup>
      <FloatNavGroup header={t("plan_terms.extra_benefits")}>
        <FloatNavLink id={PDS.HEARING} label={t("plan_terms.hearing")} />
        <FloatNavLink
          id={PDS.PREVENTIVE_DENTAL}
          label={t("plan_terms.preventive_dental.short")}
        />
        <FloatNavLink
          id={PDS.COMPREHENSIVE_DENTAL}
          label={t("plan_terms.comprehensive_dental.short")}
        />
        <FloatNavLink id={PDS.VISION} label={t("plan_terms.vision")} />
        <FloatNavLink
          id={PDS.NON_OPIOID_PAIN_MANAGEMENT}
          label={t("plan_terms.non_opioid_pain_management")}
        />
        {hasMoreBenefits && !onlyMoreBenefitIsOpioidTreatment && (
          <FloatNavLink
            id={PDS.MORE_BENEFITS}
            label={t("more_benefits.title")}
          />
        )}
        {hasSpecialBenefits && (
          <FloatNavLink
            id={PDS.SPECIAL_BENEFITS}
            label={t("special_benefit.special_benefits")}
          />
        )}
        {planDataUtils.showMMPBenefits(plan) && (
          <FloatNavLink
            id={PDS.MMP_ADDITIONAL_BENEFITS}
            label={t(
              "plan_terms.navigation.mmp_additional_benefits_and_services"
            )}
          />
        )}
      </FloatNavGroup>
      {hasOptionalBenefits && (
        <FloatNavGroup header={t("optional_packages.title")}>
          <FloatNavLink
            id={PDS.OPTIONAL_PACKAGES}
            label={t("optional_packages.title")}
          />
        </FloatNavGroup>
      )}
      <FloatNavGroup header={t("plan_terms.drug_coverage_and_costs")}>
        {hasAbstractBenefits && (
          <FloatNavLink
            id={PDS.COSTS_BY_DRUG_TIER}
            label={t("abstract_benefits.title")}
          />
        )}
        {hasDrugs && (
          <FloatNavLink
            id={PDS.PHARMACIES}
            label={t("plan_card.section_heading.pharmacies")}
          />
        )}
        {hasDrugs && hasDrugCosts && (
          <FloatNavLink
            id={PDS.ESTIMATED_DRUG_COSTS}
            label={t("plan_details.drug_costs.estimated_coverage")}
          />
        )}
        {hasDrugs && hasDrugCosts && (
          <FloatNavLink
            id={PDS.OTHER_DRUG_INFO}
            label={t("plan_terms.other_drug_information")}
          />
        )}
        {hasDrugs && (
          <FloatNavLink
            id={PDS.MY_DRUG_LIST}
            label={t("plan_terms.my_drug_list")}
          />
        )}
        <FloatNavLink
          id={PDS.PART_B_DRUGS}
          label={t("plan_terms.medicare_part_b_drugs")}
        />
      </FloatNavGroup>
      <FloatNavGroup header={t("plan_stars.medicare.star_ratings")}>
        <FloatNavLink
          id={PDS.STAR_RATINGS}
          label={t("plan_stars.medicare.star_ratings")}
        />
      </FloatNavGroup>
      <FloatNavGroup header={t("plan_terms.documents_and_contact_information")}>
        <FloatNavLink
          id={PDS.CONTACT_INFO}
          label={t("plan_terms.contact_information")}
        />
      </FloatNavGroup>
    </FloatNav>
  );
};

export default Navigation;
